import { io } from 'socket.io-client';
import { auth } from '../firebase.config';
import { chatConfig } from '../config/chatConfig';

class ChatService {
    constructor() {
        this.socket = null;
        this.isConnected = false;
        this.baseURL = chatConfig.backendUrl;
    }

    async connect() {
        try {
            // Get Firebase Auth token
            const user = auth.currentUser;
            if (!user) {
                throw new Error('User not authenticated');
            }

            const token = await user.getIdToken();
            
            this.socket = io(this.baseURL, {
                auth: {
                    token: token
                },
                ...chatConfig.socketConfig
            });

            this.socket.on('connect', () => {
                // console.log('Connected to chat server');
                this.isConnected = true;
            });

            this.socket.on('disconnect', () => {
                console.log('Disconnected from chat server');
                this.isConnected = false;
            });

            this.socket.on('error', (error) => {
                console.error('Socket error:', error);
            });

            this.socket.on('connect_error', (error) => {
                console.error('Connection error:', error);
            });

            return this.socket;
        } catch (error) {
            console.error('Failed to connect to chat server:', error);
            throw error;
        }
    }

    disconnect() {
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
            this.isConnected = false;
        }
    }

    // Join a conversation room
    joinConversation(conversationId) {
        if (this.socket && this.isConnected) {
            this.socket.emit('join_conversation', { conversationId });
        }
    }

    // Leave a conversation room
    leaveConversation(conversationId) {
        if (this.socket && this.isConnected) {
            this.socket.emit('leave_conversation', { conversationId });
        }
    }

    // Send a message
    sendMessage(conversationId, content, messageType = 'text') {
        if (this.socket && this.isConnected) {
            this.socket.emit('send_message', {
                conversationId,
                content,
                messageType
            });
        }
    }

    // Start typing indicator
    startTyping(conversationId) {
        if (this.socket && this.isConnected) {
            this.socket.emit('typing_start', { conversationId });
        }
    }

    // Stop typing indicator
    stopTyping(conversationId) {
        if (this.socket && this.isConnected) {
            this.socket.emit('typing_stop', { conversationId });
        }
    }

    // Mark messages as read
    markMessagesAsRead(conversationId, messageIds) {
        if (this.socket && this.isConnected) {
            this.socket.emit('mark_messages_read', {
                conversationId,
                messageIds
            });
        }
    }

    // Listen for new messages
    onNewMessage(callback) {
        if (this.socket) {
            this.socket.on('new_message', callback);
        }
    }

    // Listen for typing indicators
    onUserTyping(callback) {
        if (this.socket) {
            this.socket.on('user_typing', callback);
        }
    }

    // Listen for read receipts
    onMessagesRead(callback) {
        if (this.socket) {
            this.socket.on('messages_read', callback);
        }
    }

    // Listen for connection status
    onConnectionStatus(callback) {
        if (this.socket) {
            this.socket.on('connect', () => callback(true));
            this.socket.on('disconnect', () => callback(false));
        }
    }

    // Remove all listeners
    removeAllListeners() {
        if (this.socket) {
            this.socket.removeAllListeners();
        }
    }

    // Get connection status
    getConnectionStatus() {
        return this.isConnected;
    }
}

const chatService = new ChatService();
export default chatService;
